"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CircleCheck as CheckCircle, ShieldCheck, Zap } from 'lucide-react';
export default function Benefits() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const buyerSteps = [{
    title: "Vetted Network",
    description: "Access only verified manufacturers and wholesalers globally.",
    icon: <ShieldCheck className="size-6 text-accent" />
  }, {
    title: "Direct Sourcing",
    description: "Eliminate middlemen and negotiate directly with suppliers.",
    icon: <Zap className="size-6 text-accent" />
  }, {
    title: "Secure Logistics",
    description: "End-to-end support from negotiation to final delivery.",
    icon: <CheckCircle className="size-6 text-accent" />
  }];
  return <section data-section-id="14711" id="buyer-benefits" className="bg-primary-100 text-primary-900 py-32 md:py-40 px-6 md:px-10 lg:px-16">
      <div className="mx-auto max-w-[1200px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20 items-center">
          <div className="lg:col-span-7">
            <div ref={headingRef} className="transition-all duration-700 ease-out" style={{
            opacity: headingVisible ? 1 : 0,
            transform: headingVisible ? "translateY(0)" : "translateY(12px)",
            transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
          }}>
              <p className="font-default text-[0.8125rem] font-medium uppercase tracking-[0.12em] text-primary-900/60" style={{
              letterSpacing: "0.12em"
            }}>
                Global Sourcing Excellence
              </p>

              <h2 className="font-default mt-6 text-[clamp(2.25rem,4vw,3.5rem)] font-bold leading-[1.1] tracking-[-0.02em] text-primary-900">
                Streamline your supply chain with{" "}
                <em className="italic font-normal transition-opacity duration-500" style={{
                color: "var(--color-accent)",
                opacity: headingVisible ? 1 : 0,
                transitionDelay: headingVisible ? "200ms" : "0ms"
              }}>
                  verified
                </em>
                {" "}global suppliers
              </h2>

              <p className="font-default mt-8 max-w-[600px] text-[clamp(1.125rem,1.3vw,1.25rem)] leading-[1.65] text-primary-900/80">
                Access a curated network of vetted manufacturers and wholesalers. From bulk trading negotiations to end-to-end logistics support, we provide the infrastructure corporate buyers need to scale procurement with confidence and precision.
              </p>

              <div className="mt-10">
                <Button variant="default" size="lg" className="btn-primary" asChild>
                  <Link to="https://wa.me/971529096913" newTab>
                    Start sourcing now →
                  </Link>
                </Button>
              </div>

              <div className="mt-20">
                <h3 className="text-2xl font-semibold text-primary-900 mb-8">
                  Why Buyers Choose KTEX
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {buyerSteps.map((step, i) => <div key={i} data-index={i} className="card flex flex-col gap-4">
                      <div className="p-2 bg-primary-100 rounded-lg w-fit">
                        {step.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-primary-900 mb-2">
                          {step.title}
                        </h4>
                        <p className="text-sm text-primary-900/70 leading-relaxed mb-0">
                          {step.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="aspect-square rounded-lg overflow-hidden shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/global_logistics_sourcing.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/global_logistics_sourcing.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/global_logistics_sourcing.png-wvc-sizes" alt="Professional logistics and global trade management" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}